/**
 * Hook to manage module preferences (recently accessed, favorites, collapsed groups, view mode)
 * Persisted in localStorage
 */

import { useState, useEffect, useCallback } from "react";

interface RecentItem {
  name: string;
  href: string;
  moduleName: string;
  timestamp: number;
}

interface ModulePreferences {
  recentItems: RecentItem[];
  favorites: string[]; // Array of item hrefs
  collapsedGroups: Record<string, string[]>; // moduleSlug -> array of collapsed group titles
  viewMode: "grid" | "list";
}

const STORAGE_KEY = "module-preferences";
const MAX_RECENT_ITEMS = 10;

function getDefaultPreferences(): ModulePreferences {
  return {
    recentItems: [],
    favorites: [],
    collapsedGroups: {},
    viewMode: "grid",
  };
}

function loadPreferences(): ModulePreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...getDefaultPreferences(), ...parsed };
    }
  } catch (e) {
    console.error("Failed to load module preferences:", e);
  }
  return getDefaultPreferences();
}

function savePreferences(prefs: ModulePreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch (e) {
    console.error("Failed to save module preferences:", e);
  }
}

export function useModulePreferences(moduleSlug?: string) {
  const [preferences, setPreferences] = useState<ModulePreferences>(getDefaultPreferences);

  // Load preferences on mount
  useEffect(() => {
    setPreferences(loadPreferences());
  }, []);

  // Add item to recently accessed
  const addRecentItem = useCallback((item: { name: string; href: string; moduleName: string }) => {
    setPreferences((prev) => {
      // Remove existing entry for this href
      const filtered = prev.recentItems.filter((r) => r.href !== item.href);

      // Add new entry at the beginning
      const newRecent: RecentItem = {
        ...item,
        timestamp: Date.now(),
      };

      const updated = [newRecent, ...filtered].slice(0, MAX_RECENT_ITEMS);

      const newPrefs = { ...prev, recentItems: updated };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, []);

  // Get recent items for a specific module
  const getRecentItemsForModule = useCallback(
    (moduleName: string, limit = 6) => {
      return preferences.recentItems
        .filter((item) => item.moduleName === moduleName)
        .slice(0, limit);
    },
    [preferences.recentItems]
  );

  // Toggle favorite
  const toggleFavorite = useCallback((href: string) => {
    setPreferences((prev) => {
      const isFavorite = prev.favorites.includes(href);
      const newFavorites = isFavorite
        ? prev.favorites.filter((f) => f !== href)
        : [...prev.favorites, href];

      const newPrefs = { ...prev, favorites: newFavorites };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, []);

  // Check if item is favorite
  const isFavorite = useCallback(
    (href: string) => preferences.favorites.includes(href),
    [preferences.favorites]
  );

  // Get all favorites for a module
  const getFavoritesForModule = useCallback(
    (_moduleName: string, allItems: { name: string; href: string }[]) => {
      return allItems.filter((item) => preferences.favorites.includes(item.href));
    },
    [preferences.favorites]
  );

  // Toggle group collapsed state
  const toggleGroupCollapsed = useCallback(
    (groupTitle: string) => {
      if (!moduleSlug) return;

      setPreferences((prev) => {
        const moduleCollapsed = prev.collapsedGroups[moduleSlug] || [];
        const isCollapsed = moduleCollapsed.includes(groupTitle);

        const newModuleCollapsed = isCollapsed
          ? moduleCollapsed.filter((g) => g !== groupTitle)
          : [...moduleCollapsed, groupTitle];

        const newPrefs = {
          ...prev,
          collapsedGroups: {
            ...prev.collapsedGroups,
            [moduleSlug]: newModuleCollapsed,
          },
        };
        savePreferences(newPrefs);
        return newPrefs;
      });
    },
    [moduleSlug]
  );

  // Check if group is collapsed
  const isGroupCollapsed = useCallback(
    (groupTitle: string) => {
      if (!moduleSlug) return false;
      return (preferences.collapsedGroups[moduleSlug] || []).includes(groupTitle);
    },
    [moduleSlug, preferences.collapsedGroups]
  );

  // Set view mode
  const setViewMode = useCallback((mode: "grid" | "list") => {
    setPreferences((prev) => {
      const newPrefs = { ...prev, viewMode: mode };
      savePreferences(newPrefs);
      return newPrefs;
    });
  }, []);

  return {
    // Recent items
    recentItems: preferences.recentItems,
    addRecentItem,
    getRecentItemsForModule,

    // Favorites
    favorites: preferences.favorites,
    toggleFavorite,
    isFavorite,
    getFavoritesForModule,

    // Collapsed groups
    toggleGroupCollapsed,
    isGroupCollapsed,

    // View mode
    viewMode: preferences.viewMode,
    setViewMode,
  };
}
